import React, { useState } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import FilterChips from '../components/shared/FilterChips';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import { cssUrl } from '../utils/cssVersion';
import { CalendarIcon, UserIcon, PinIcon, DollarIcon, ClockIcon, SwapIcon, CloseIcon } from '../components/shared/Icons';
import { useUser } from '../utils/UserContext';
import { rolesAccess } from '../data/map';
import {
  fetchPeriodical,
  handlePeriodicalMgmtSubmit,
  handlePeriodicalCleanerSubmit,
  downloadPeriodical,
} from '../utils/api';
import { formatCurrency, parseCurrency } from '../utils/format';
import { CHIP_GROUP_COLORS } from '../components/shared/chipColors';

const filterLeaveStyles = cssUrl('filterTemplate.css');



// ===== QANTAS — planes-driven quote =====
const QANTAS_SITE = 'Qantas Jet Base';
const PLANES_OPTIONS = ['0', '1', '2', '3'];
const PLANES_QUOTE_MAP = {
  '0': '1423.03',
  '1': '1039.91',
  '2': '820.98',
  '3': '419',
};

const displayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ===== HELPER: Is a YYYY-MM-DD string in a prior month relative to today? =====
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};

// ─── PERIODICAL CARD ───
const PeriodicalCard = ({ item, onClick, isAdmin, isSupervisor }) => {

  return (
    <div className="fr-card" onClick={() => onClick(item)}>
      <div className="fr-card-header">
        <div className="fr-card-dates">
          <CalendarIcon />
          <span className="fr-card-date-start">{displayDate(item[2])}</span>
          <span className="fr-card-date-sep">→</span>
          <span className="fr-card-date-end">{displayDate(item[3])}</span>
        </div>
        <span className="fr-card-status-tags" >
            Cleaners: {formatCurrency(item[12])}
          </span>
        <div className="fr-card-status-badge-extra">
          {item[21]}
        </div>
      </div>

      <div className="fr-card-body">
        <div className="fr-card-row">
          <PinIcon />
          <span className="fr-card-label">Site:</span>
          <span className="fr-card-value">{item[4]}</span>
          <span className="fr-card-status-tag">
            Automate: {item[20]}
          </span>
        </div>

        <div className="fr-card-row">
          <SwapIcon />
          <span className="fr-card-label">Cycle:</span>
          <span className="fr-card-value">{item[19]}</span>
          <span className="fr-card-status-tag" data-status={item[14]}>
            Rate: {formatCurrency(item[14])}
          </span>
        </div>

        <div className="fr-card-row">
          <UserIcon />
          <span className="fr-card-label">Attending:</span>
          <span className="fr-card-value">{item[13]}</span>

        </div>

        {isAdmin || isSupervisor ? (
          <div className="fr-card-row">
            <DollarIcon />
            <span className="fr-card-label">Quote:</span>
            <span className="fr-card-value">{formatCurrency(item[5])}</span>
            <span className="fr-card-status-tag">
            SNL: {formatCurrency(item[11])}
          </span>
          </div>
        ) : (
          <div className="fr-card-row">
            <DollarIcon />
            <span className="fr-card-label">Cleaners:</span>
            <span className="fr-card-value">{formatCurrency(item[12])}</span>
          </div>
        )}
      </div>

      <div className="fr-card-footer">
        <div className="fr-card-meta">
          <span className="fr-card-meta-item">
            <UserIcon />
            Cleaners: {item[6]}
          </span>
          <span className="fr-card-meta-item">
            <DollarIcon />
            Cleaners Payment: {formatCurrency(item[12])}
          </span>
        </div>

          <div className="fr-card-meta">
            <span className="fr-card-meta-item">Periodical: {item[1]}</span>
          </div>

        <div className="fr-card-hours">
          <ClockIcon />
          <span>{item[15]} days</span>
        </div>
      </div>

      <div className="fr-card-tap-hint">Tap to update</div>
    </div>
  );
};

// ─── MANAGEMENT EDIT MODAL ───
const PeriodicalMgmtModal = ({ item, onClose, onSubmit, isSubmitting, onToast, isAdmin, isSupervisor, canEditPriorDates }) => {
  const [periodical, setPeriodical] = useState(item?.[1] || '');
  const [site, setSite] = useState(item?.[4] || '');
  const [startDate, setStartDate] = useState(item?.[2] || '');
  const [endDate, setEndDate] = useState(item?.[3] || '');
  const [cycleType, setCycleType] = useState(item?.[19] || 'Once-Off');

  
  const [quote, setQuote] = useState(formatCurrency(item?.[5] ?? ''));
  const [cleaners, setCleaners] = useState(item?.[6] || '');
  const [rework, setRework] = useState(formatCurrency(item?.[7] ?? ''));
  const [chemical, setChemical] = useState(formatCurrency(item?.[8] ?? ''));
  const [equipment, setEquipment] = useState(formatCurrency(item?.[9] ?? ''));
  const [incidental, setIncidental] = useState(formatCurrency(item?.[10] ?? ''));
  const [attend, setAttend] = useState(item?.[13] || '');
  const [rate, setRate] = useState(formatCurrency(item?.[14] ?? ''));
  const [days, setDays] = useState(item?.[15] ?? '');
  const [reworkPercent, setReworkPercent] = useState(item?.[16] ?? '');
  const [percentRate, setPercentRate] = useState(item?.[17] ?? '');
  const [workType, setWorkType] = useState(item?.[18] || 'TFN');
  const [workStatus, setWorkStatus] = useState(item?.[21] || 'Pending');

  // ── Qantas: planes drive the quote, quote becomes read-only ──
  const isQantasSite = (item?.[4] || '') === QANTAS_SITE;
  const [planes, setPlanes] = useState(String(item?.[22] ?? '') || '0');

  const handlePlanesChange = (val) => {
    setPlanes(val);
    setQuote(formatCurrency(PLANES_QUOTE_MAP[val]));
  };

  const cycleOptions = [
    'Once-Off', 'Monthly', 'Call-Out', 'Please Delete', '6-Monthly',
    'Quarterly', 'Emergency-Call', 'Yearly', 'Value-Added', 'Weekly',
    'Fortnight', 'Bi-Monthly'
  ];
  const workTypeOptions = ['TFN', 'ABN'];
  const workStatusOptions = ['Pending', 'In Progress', 'Completed'];

  const currencyInputProps = (value, setter) => ({
  className: 'fr-modal-input',
  inputMode: 'decimal',
  value,
  onChange: (e) => {
    const v = e.target.value;
    if (v.includes('-')) return;
    setter(v);
  },
  onFocus: (e) => setter(parseCurrency(e.target.value)),
  onBlur:  (e) => setter(formatCurrency(e.target.value)),
});

  
  const recordIsPriorMonth = isPriorMonth(item?.[2]) || isPriorMonth(item?.[3]);
  const datesReadOnly = recordIsPriorMonth && !canEditPriorDates;

  const clearDates = () => {
    setStartDate('');
    setEndDate('');
  };

  const handleStartDateChange = (val) => {
    if (!val) {
      setStartDate('');
      return;
    }

    if (!canEditPriorDates && isPriorMonth(val)) {
      onToast({ message: 'Cannot set periodical dates to a prior month.', type: 'warning' });
      clearDates();
      return;
    }

    setStartDate(val);
    setEndDate(val);
  };

  const handleEndDateChange = (val) => {
    if (!val) {
      setEndDate('');
      return;
    }

    if (!canEditPriorDates && isPriorMonth(val)) {
      onToast({ message: 'Cannot set periodical dates to a prior month.', type: 'warning' });
      clearDates();
      return;
    }

    let startVal = startDate;
    let endVal = val;

    if (!startVal) {
      setStartDate(endVal);
      setEndDate(endVal);
      return;
    }

    if (endVal < startVal) {
      onToast({
        message: 'End date cannot be before start date. Change the start date to shift the range.',
        type: 'warning',
      });
      return;
    }

    const [sy, sm] = startVal.split('-').map(Number);
    const [ey, em] = endVal.split('-').map(Number);
    if (sy !== ey || sm !== em) {
      const lastDay = getLastDayOfMonth(sy, sm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      endVal = `${sy}-${pad(sm)}-${pad(lastDay)}`;
      onToast({
        message: `End date must stay within the same month. Clamped to ${lastDay}th.`,
        type: 'warning',
      });
    }

    setEndDate(endVal);
  };

  
  const toNumberString = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const cleaned = String(val).replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return '';
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : String(num);
  };
const handleNonNegative = (setter) => (e) => {
  const v = e.target.value;
  if (v === '' || Number(v) >= 0) setter(v);
};
  const handleSubmit = () => {
    if (!canEditPriorDates && !recordIsPriorMonth) {
      if (isPriorMonth(startDate) || isPriorMonth(endDate)) {
        onToast({ message: 'Dates cannot be in a prior month.', type: 'warning' });
        return;
      }
    }


    const data = {
      recordids: item[0],
      period: periodical,
      site,
      datestart: startDate,
      dateend: endDate,
      cycletype2: cycleType,
      quote:      toNumberString(quote),
      cleaners: toNumberString(cleaners),
      rework:     toNumberString(rework),
      chemical:   toNumberString(chemical),
      equipment:  toNumberString(equipment),
      incidental: toNumberString(incidental),
      attend,
      rate:       toNumberString(rate),
      days: toNumberString(days),
      reworkpercent: toNumberString(reworkPercent),
      percentrate1: toNumberString(percentRate),
      worktype: workType,
      workstatus2: workStatus,
      planes,
    };
    onSubmit(data);
  };

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">Management - Edit Periodical</h3>
          <button className="fr-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="fr-modal-body">
          <div className="fr-modal-field">
            <label className="fr-modal-label">Periodical</label>
            <textarea
              className="fr-modal-input"
              value={periodical}
              readOnly
              style={{ height: '80px', resize: 'none' }}
            />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Site</label>
            <input className="fr-modal-input" value={site} readOnly onChange={(e) => setSite(e.target.value)} />
          </div>

          {datesReadOnly ? (
            <>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Start Date</label>
                <div className="fr-modal-value">{displayDate(startDate)}</div>
              </div>
              <div className="fr-modal-field">
                <label className="fr-modal-label">End Date</label>
                <div className="fr-modal-value">{displayDate(endDate)}</div>
              </div>
              <div className="fr-modal-field" style={{ fontSize: '0.78rem', color: 'rgba(251,191,36,0.85)', marginTop: '-4px' }}>
                Dates are read-only — record is from a prior month.
              </div>
            </>
          ) : (
            <>
              <div className="fr-modal-field">
                <label className="fr-modal-label">Start Date</label>
                <input
                  type="date"
                  className="fr-modal-input"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </div>
              <div className="fr-modal-field">
                <label className="fr-modal-label">End Date</label>
                <input
                  type="date"
                  className="fr-modal-input"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                />
              </div>
            </>
          )}

          <CustomSelect
            label="Periodical Cycle"
            value={cycleType}
            onChange={setCycleType}
            options={cycleOptions}
          />

          {/* ── Qantas only: planes select drives the quote ── */}
          {isQantasSite && (
            <CustomSelect
              label="Number of Planes"
              value={planes}
              onChange={handlePlanesChange}
              options={PLANES_OPTIONS}
            />
          )}

          {/* ── Currency fields use the focus/blur formatting pattern ── */}
          <div className="fr-modal-field">
            <label className="fr-modal-label">Quote</label>
            {isQantasSite ? (
              <input className="fr-modal-input" value={quote} readOnly />
            ) : (
              <input {...currencyInputProps(quote, setQuote)} />
            )}
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Number of Cleaners</label>
            <input className="fr-modal-input" 
                type="number"
                step="0.25"
                min="0"
                value={cleaners} 
                onChange={handleNonNegative(setCleaners)}
                />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Rework Value</label>
            <input {...currencyInputProps(rework, setRework)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Chemicals</label>
            <input {...currencyInputProps(chemical, setChemical)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Equipment</label>
            <input {...currencyInputProps(equipment, setEquipment)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Incidental</label>
            <input {...currencyInputProps(incidental, setIncidental)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Cleaner Attending</label>
            <input className="fr-modal-input" value={attend} onChange={(e) => setAttend(e.target.value)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Rate</label>
            <input {...currencyInputProps(rate, setRate)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Completion Days</label>
            <input className="fr-modal-input" 
                type="number"
                step="0.25"
                min="0"
                value={days} onChange={handleNonNegative(setDays)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Rework Percent</label>
            <input className="fr-modal-input" value={reworkPercent} onChange={handleNonNegative(setReworkPercent)} />
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Percent Rate</label>
            <input className="fr-modal-input" value={percentRate} onChange={handleNonNegative(setPercentRate)} />
          </div>

          <CustomSelect
            label="Work Type"
            value={workType}
            onChange={setWorkType}
            options={workTypeOptions}
          />
          <CustomSelect
            label="Work Status"
            value={workStatus}
            onChange={setWorkStatus}
            options={workStatusOptions}
          />
        </div>

        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="fr-modal-btn fr-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Submit Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── EMPLOYEE / SUBCONTRACTOR EDIT MODAL ───
const PeriodicalCleanerModal = ({ item, onClose, onSubmit, isSubmitting, user}) => {
  const [workStatus, setWorkStatus] = useState(item?.[21] || 'Pending');
  const [attend, setAttend] = useState(
    item?.[13] === 'Cleaner Name' ? (user?.userName) : (item?.[13])
  );

  // ── Qantas: planes select shows for everyone; quote is
  //    populated silently in the background and submitted with the update ──
  const isQantasSite = (item?.[4] || '') === QANTAS_SITE;
  const [planes, setPlanes] = useState(String(item?.[22] ?? '') || '0');

  const toNumberString = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const cleaned = String(val).replace(/[$,\s]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? '' : String(num);
  };

  const [quote, setQuote] = useState(toNumberString(item?.[5]));

  const handlePlanesChange = (val) => {
    setPlanes(val);
    setQuote(PLANES_QUOTE_MAP[val]);
  };

  const workStatusOptions = ['Pending', 'In Progress', 'Completed'];

  const handleSubmit = () => {
    const data = {
      recordids: item[0],
      period: item[1],
      site: item[4],
      datestart: item[2],
      dateend: item[3],
      workstatus3: workStatus,
      attend,
      ...(isQantasSite ? { planes, quote } : {}),
    };
    onSubmit(data);
  };

  return (
    <div className="fr-modal-overlay" onClick={onClose}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-handle" />
        <div className="fr-modal-header">
          <h3 className="fr-modal-title">Update Periodical</h3>
          <button className="fr-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="fr-modal-body">
          <div className="fr-modal-field">
            <label className="fr-modal-label">Periodical</label>
            <div className="fr-modal-value">{item[1]}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Client</label>
            <div className="fr-modal-value">{item[4]}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">Start Date</label>
            <div className="fr-modal-value">{displayDate(item[2])}</div>
          </div>
          <div className="fr-modal-field">
            <label className="fr-modal-label">End Date</label>
            <div className="fr-modal-value">{displayDate(item[3])}</div>
          </div>

          <CustomSelect
            label="Work Status"
            value={workStatus}
            onChange={setWorkStatus}
            options={workStatusOptions}
          />

          {/* ── Qantas only: number of planes (quote updates silently) ── */}
          {isQantasSite && (
            <CustomSelect
              label="Number of Planes"
              value={planes}
              onChange={handlePlanesChange}
              options={PLANES_OPTIONS}
            />
          )}

          <div className="fr-modal-field">
            <label className="fr-modal-label">Cleaner</label>
            <input className="fr-modal-input" value={attend} onChange={(e) => setAttend(e.target.value)} />
          </div>
        </div>

        <div className="fr-modal-actions">
          <button className="fr-modal-btn fr-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="fr-modal-btn fr-btn-submit" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Submit Update'}
          </button>
        </div>
      </div>
    </div>
  );
};



const filterChipGroups = [
  {
    color: CHIP_GROUP_COLORS.cyan,
    values: ['Pending', 'InProgress', 'Completed','Once-Off', 'Monthly', 'Call-Out', 'Quarterly','6-Monthly', 'Yearly', 'Weekly', 'Fortnight','Emergency-Call', 'Value-Added', 'Bi-Monthly', 'Please Delete'],
  },

];


const FilterPeriodical = ({ showHeader = false }) => {
  const { user } = useUser();
  const isIt = rolesAccess.it.includes(user?.userJob || '');
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');
  const isSupervisor = rolesAccess.supervisor.includes(user?.userJob || '');
  const isManagement = rolesAccess.management.includes(user?.userJob || '');

  const isMgmtView = isAdmin || isSupervisor || isManagement;

  // Only IT can edit prior-month dates — the Airtable rollover automation
  // depends on prior cycles staying put (e.g. 3-fortnight months like July
  // 2026 were being collapsed to just the third cycle). Everyone else can
  // still edit dates forward as normal.
  const canEditPriorDates = isIt;

  return (
    <FilterTemplate
      title="Periodical"
      subtitle="Periodical Work"
      searchPlaceholder="Search by site, periodical, status..."
      stylesheetHref={filterLeaveStyles}
      fetchData={fetchPeriodical}
      dataKey="periodicals"
      searchFields={[1, 2, 4, 13, 19, 21]}
      onSubmit={
        isMgmtView
          ? handlePeriodicalMgmtSubmit
          : handlePeriodicalCleanerSubmit
      }
      loadingMessage="Loading periodicals..."
      showNavigation={true}
      showHeader={showHeader}
      chipLogic="or"
      onDownload={isManagement && downloadPeriodical}
      helpButton={
        <HelpButton helpData={[
          { chip: 'periodical_filter', urlFilter: '/static/pdffilters/PeriodicalFilterGuide.pdf' }
        ]} />
      }
      renderChips={(chipQuery, setChipQuery, chipsOpen, setChipsOpen) => (
        <FilterChips
          chipGroups={filterChipGroups}
          chipQuery={chipQuery}
          setChipQuery={setChipQuery}
          chipsOpen={chipsOpen}
          setChipsOpen={setChipsOpen}
        />
      )}
      renderCard={(item, onClick) => (
      <PeriodicalCard item={item} onClick={onClick} isAdmin={isAdmin} isSupervisor={isSupervisor || isManagement}/>
    )}
      renderModal={(item, onClose, onSubmit, isSubmitting, setToast) =>
         isMgmtView ? (
          <PeriodicalMgmtModal
            item={item}
            onClose={onClose}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            onToast={setToast}
            isAdmin={isAdmin}
            isSupervisor={isSupervisor}
            canEditPriorDates={canEditPriorDates}
          />
        ) : (
          <PeriodicalCleanerModal
            item={item}
            onClose={onClose}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            user={user}
          />
        )
      }
    />
  );
};

export default FilterPeriodical;